import { useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Table,
  Modal,
  Text,
  Stack,
  Loader,
  Badge,
} from "@mantine/core";
import { IconPencil, IconTrash, IconMessages } from "@tabler/icons-react";
import { Link } from "wouter";
import { notifications } from "@mantine/notifications";
import { getPanels, deletePanel } from "./panelApi";

const protocolLabel = {
  roundtable: "Roundtable",
  debate: "Debate",
  redTeam: "Red-team",
};

export default function PanelList() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);

  const fetchPanels = async () => {
    setLoading(true);
    try {
      setPanels(await getPanels());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPanels();
  }, []);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await deletePanel(deleteModal.id);
      setDeleteModal(null);
      fetchPanels();
    } catch (err) {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Modal
        opened={deleteModal != null}
        onClose={() => setDeleteModal(null)}
        title="Delete panel"
      >
        {deleteModal && (
          <Stack>
            <Text size="sm">
              Delete{" "}
              <Text span fw={700}>
                {deleteModal.name}
              </Text>
              ? Existing conversations keep their frozen copy and are unaffected.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDeleteModal(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmDelete}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Group justify="space-between">
        <h2>Panels</h2>
        <Group>
          <Button component={Link} to="/panels" variant="subtle">
            Open chat
          </Button>
          <Button component={Link} to="/panels/manage/new" variant="outline">
            New panel
          </Button>
        </Group>
      </Group>

      {panels.length === 0 ? (
        <Text c="dimmed">
          No panels yet. Create one to convene a group of models on a hard
          question.
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Protocol</Table.Th>
              <Table.Th>Seats</Table.Th>
              <Table.Th>Chair</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {panels.map((panel) => (
              <Table.Tr key={panel.id}>
                <Table.Td>
                  <Text fw={500}>{panel.name}</Text>
                  {panel.description && (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {panel.description}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge variant="light">
                    {protocolLabel[panel.protocol] ?? panel.protocol}
                  </Badge>
                </Table.Td>
                <Table.Td>{panel.panelists?.length ?? 0}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {panel.chair?.model}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group wrap="nowrap" gap="xs">
                    <ActionIcon
                      variant="light"
                      color="teal"
                      title="Open chat"
                      component={Link}
                      to="/panels"
                    >
                      <IconMessages size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="default"
                      component={Link}
                      to={`/panels/manage/${panel.id}`}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => setDeleteModal(panel)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </div>
  );
}
